import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { 
  ArrowLeft, 
  Package, 
  CheckCircle2, 
  Circle,
  Loader2,
  MapPin,
  Send
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_API_URL;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons
const createCustomIcon = (isSelected) => {
  const color = isSelected ? '#67A43C' : '#B1336E';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Map center updater component
function MapCenterUpdater({ center }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  
  return null;
}

function ChooseDeliveries({ userId, setActiveComponent }) {
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mapCenter, setMapCenter] = useState([-12.0464, -77.0428]); // Lima default

  useEffect(() => {
    fetchAvailableDeliveries();
  }, []);

  const fetchAvailableDeliveries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/available-deliveries-today`);
      
      if (response.data && response.data.length > 0) {
        setDeliveryPoints(response.data);
        
        // Center map on first delivery point
        const firstPoint = response.data[0];
        if (firstPoint.latitude && firstPoint.longitude) {
          setMapCenter([firstPoint.latitude, firstPoint.longitude]);
        }
      }
    } catch (error) {
      console.error('Error fetching available deliveries:', error);
      alert('Error al cargar entregas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (pointId) => {
    setSelectedIds(prev => {
      if (prev.includes(pointId)) {
        return prev.filter(id => id !== pointId);
      } else {
        return [...prev, pointId];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      alert('Por favor selecciona al menos una entrega');
      return;
    }

    if (!window.confirm(`¿Confirmar ${selectedIds.length} entrega(s)?`)) {
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/api/assign-deliveries-to-driver`, {
        driver_id: userId,
        delivery_point_ids: selectedIds
      });

      alert('¡Entregas asignadas exitosamente!');
      setActiveComponent('MisEntregas');
      
    } catch (error) {
      console.error('Error assigning deliveries:', error);
      alert('Error al asignar entregas. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-montserrat">Cargando entregas disponibles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setActiveComponent('OptionsDriver')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl font-extrabold text-white font-montserrat">
              Elige tus Entregas
            </h1>
            <div className="w-10" />
          </div>
          
          {/* Counter */}
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span className="font-montserrat font-semibold">
                  Disponibles: {deliveryPoints.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-montserrat font-semibold">
                  Seleccionadas: {selectedIds.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {deliveryPoints.length === 0 ? (
          <div className="h-full flex items-center justify-center p-4">
            <div className="text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-700 font-montserrat mb-2">
                No hay entregas disponibles
              </h3>
              <p className="text-sm text-slate-500 font-montserrat">
                Por ahora no hay entregas en el almacén para hoy
              </p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <MapCenterUpdater center={mapCenter} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {deliveryPoints.map((point) => {
              if (!point.latitude || !point.longitude) return null;
              
              const isSelected = selectedIds.includes(point.id);
              
              return (
                <Marker
                  key={point.id}
                  position={[point.latitude, point.longitude]}
                  icon={createCustomIcon(isSelected)}
                  eventHandlers={{
                    click: () => toggleSelection(point.id)
                  }}
                >
                  <Tooltip 
                    direction="top" 
                    offset={[0, -10]}
                    opacity={0.9}
                    permanent={false}
                  >
                    <span className="text-xs font-semibold font-montserrat">
                      {point.district || 'Distrito'}
                    </span>
                  </Tooltip>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* Bottom Action Bar */}
      {selectedIds.length > 0 && (
        <div className="flex-shrink-0 bg-white border-t-2 border-slate-200 shadow-lg p-4">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-brand-primary to-pink-600 hover:from-brand-primary/90 hover:to-pink-600/90 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-lg font-montserrat"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Asignando...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Confirmar {selectedIds.length} entrega{selectedIds.length !== 1 ? 's' : ''}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Info Message */}
      {deliveryPoints.length > 0 && selectedIds.length === 0 && (
        <div className="flex-shrink-0 bg-gradient-to-r from-brand-gold/20 to-brand-primary/20 border-t border-brand-primary/30 p-4">
          <div className="flex items-start gap-3">
            <Circle className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 font-montserrat">
              <span className="font-bold">Toca los marcadores</span> en el mapa para seleccionar las entregas que deseas realizar
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChooseDeliveries;